import { agentDisplayAssociations } from '@reflex/shared';
import { ResourceChip } from '@/components/resource-preview/ResourceChip';
import { AgentAssociationChips } from '@/components/agent/AgentAssociationChips';
import { useUsers } from '@/hooks/auth/useUsers';
import { useAuth } from '@/hooks/auth/useAuth';
import type { Agent } from '@/lib/types';

/**
 * Compact identity strip shown in the agent detail header — owner,
 * organization, and (when present) the pull request. Replaces the older
 * full-width "Details" card: the same references rendered as small inline
 * chips so they live alongside the title instead of taking a whole section.
 * The created timestamp is intentionally omitted here because the header
 * already surfaces it.
 *
 * Names are resolved from the same cached sources the hover previews read
 * (`useUsers` for the owner, the active memberships for the organization),
 * so the chips show "Jane Doe" / "Runloop" instead of raw ids, and quietly
 * fall back to the id when a name can't be resolved.
 */
export function AgentDetailIdentity({ agent }: { agent: Agent }) {
  const ownerId = agent.userId;
  const orgId = agent.organizationId;
  const associations = agentDisplayAssociations(agent);
  const { data: users } = useUsers();
  const { memberships, activeMembership } = useAuth();

  if (!ownerId && !orgId && associations.length === 0) return null;

  const owner = ownerId ? users?.find((candidate) => candidate.id === ownerId) : undefined;
  const ownerName = owner?.name ?? owner?.login ?? undefined;

  const orgMembership = orgId
    ? (memberships.find((m) => m.organization.id === orgId) ?? activeMembership ?? null)
    : null;
  const orgName =
    orgMembership?.organization.id === orgId ? orgMembership?.organization.name : undefined;

  return (
    <div data-testid="agent-detail-identity" className="mt-2.5 flex flex-wrap items-center gap-1.5">
      {ownerId ? <ResourceChip kind="users" id={ownerId} name={ownerName} /> : null}
      {orgId ? <ResourceChip kind="organizations" id={orgId} name={orgName} /> : null}
      {associations.length > 0 ? <AgentAssociationChips associations={associations} /> : null}
    </div>
  );
}
